import { useContext, useEffect, useMemo } from "react";
import { ProductList } from "../ProductList";
import { PLPType } from "src/types/PLPType.d";
import algoliasearch from "algoliasearch";
import {
  BRAND_FACET,
  CATEGORIES_FACET,
  CONSCIOUS_FACET,
} from "src/utils/algolia";
import { InstantSearchServerState } from "react-instantsearch-core-v7";
import { AlgoliaSearchBrand, Gender } from "src/types/Algolia";
import { Header } from "./components/Header";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { PopularBrandsWrapper } from "../PopularBrands";
import { BrandSEO } from "src/molecules/BrandSEO";
import { RRPFootnote } from "src/molecules/RRPFootnote";
import { ShopType } from "src/types/graphql.d";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import { Locale } from "lib/Intl";
import { useBreakpoints } from "@otrium/core";
import { AlgoliaIconicProducts } from "src/modules/brands/components/AlgoliaIconicProducts";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import getDeepLink from "src/utils/getDeepLink";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { useRouter } from "next/router";
import { AppCtx } from "src/contexts/app.context";
import { useCollectionPageSchema } from "src/hooks/useCollectionPageSchema";
import {
  ContentContainer,
  HeaderContainer,
} from "../ProductList/ProductList.styled";

type BrandViewProps = {
  gender: string;
  slug: string;
  title?: string;
  indexName: string;
  locale: string;
  categoryPageId: string;
  search: {
    appId: string;
    searchKey: string;
  };
  brand: AlgoliaSearchBrand;
  serverState?: InstantSearchServerState;
};

function BrandView({
  categoryPageId,
  gender,
  indexName,
  search,
  slug,
  title,
  brand,
  locale,
  serverState,
}: BrandViewProps): JSX.Element {
  const { setPageViewedProperties } = useContext(AppCtx);
  const { i18n } = useLingui();
  const router = useRouter();

  const searchClient = useMemo(
    () => algoliasearch(search?.appId, search?.searchKey),
    [search?.appId, search?.searchKey]
  );
  const { isDesktop } = useBreakpoints();

  const genderId = useMemo(() => localeFix({ gender }), [gender]);

  const viewAllink = useMemo(
    () =>
      getAllBrandsPageRoute({
        shopType: genderId as ShopType,
        locale: locale as Locale,
      }),
    [genderId, locale]
  );

  useEffect(() => {
    if (brand) {
      setPageViewedProperties({
        page_type: "brand",
        brand: brand?.name,
        brand_id: brand.objectID,
      });
    }
  }, [genderId, brand, setPageViewedProperties]);

  const seoTitle = t(i18n)`${
    brand?.name ? `${brand?.name} | ` : ""
  }Online Outlet | Otrium`;

  // Generate schema for the collection page
  useCollectionPageSchema({
    brand,
    gender,
    slug: slug || "",
    title: seoTitle,
    description: brand?.description || "",
  });

  return (
    <>
      <PageHead
        title={seoTitle}
        deeplink={getDeepLink({
          hostname: `${getI18nRoute({
            route: `/brands`,
            locale: locale as Locale,
          })}/${slug}/${gender}`,
          query: {
            cat_slug: router.query?.cat_slug,
            discount: router.query?.discount,
          },
        })}
        description={brand?.description || ""}
        enableIndexation
      />
      <ContentContainer className="no-padding-block">
        <HeaderContainer>
          <Header
            brand={brand}
            genderId={localeFix({ gender }) as Gender}
            slug={slug}
          />

          <AlgoliaIconicProducts
            genderId={gender}
            localeGenderId={genderId}
            slug={slug}
            plpType={PLPType.BRAND_PLP}
            noPadding
          />
        </HeaderContainer>

        <ProductList
          title={title}
          categoryPageId={categoryPageId}
          gender={gender}
          indexName={indexName}
          pageContentType="BRAND_PLP"
          searchClient={searchClient}
          slug={slug}
          excludeFilters={
            isDesktop
              ? [CATEGORIES_FACET, BRAND_FACET, CONSCIOUS_FACET]
              : [BRAND_FACET, CONSCIOUS_FACET]
          }
          serverState={serverState}
        />

        <PopularBrandsWrapper
          genderId={gender}
          localeGenderId={genderId}
          excludeSlug={slug}
          viewAllLink={viewAllink}
          plpType={PLPType.BRAND_PLP}
        />

        <BrandSEO brandSlug={slug} shopType={genderId as ShopType} />
      </ContentContainer>

      <RRPFootnote />
    </>
  );
}

export { BrandView };
